/** @jsx jsx */
import { useMemo, useState } from 'react';

import { jsx } from '@emotion/react';
import { connectSearchBox } from 'react-instantsearch-dom';
import debounce from 'lodash/debounce';

import VisuallyHidden from '@atlaskit/visually-hidden';
import { Box, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { N200 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import SearchIcon from '@atlaskit/icon/glyph/search';
import type { SearchBoxProvided } from 'react-instantsearch-core';

const SEARCH_DEBOUNCE_MS = 350;

const searchWrapperStyles = xcss({
  flex: '1 1 auto',

  // @ts-expect-error hardcode input font size
  input: {
    // Safari zooms if the input has smaller text
    font: token(
      'font.body.large',
      'normal 400 16px/24px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, system-ui, "Helvetica Neue", sans-serif',
    ),
  },
});

type SearchInputProps = {
  testId?: string;
} & SearchBoxProvided;

const SearchInput = connectSearchBox<SearchInputProps>(({ refine, testId }) => {
  const [inputValue, setInputValue] = useState('');

  const handleChange = useMemo(
    () =>
      debounce((inputValue: string) => {
        refine(inputValue);
      }, SEARCH_DEBOUNCE_MS),
    [refine],
  );

  return (
    <Box xcss={searchWrapperStyles}>
      <VisuallyHidden>
        <label htmlFor="search-input">Search</label>
      </VisuallyHidden>
      <Textfield
        testId={testId ? `${testId}-search-input` : undefined}
        id="search-input"
        onChange={(e) => {
          setInputValue(e.currentTarget.value);
          handleChange(e.currentTarget.value);
        }}
        placeholder="Search"
        value={inputValue}
        elemBeforeInput={
          <Box as="span" paddingInlineStart="space.100">
            <SearchIcon label="" primaryColor={token('color.icon', N200)} />
          </Box>
        }
        autoComplete="off"
      />
    </Box>
  );
});

export default SearchInput;
